// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { CircularProgress, Tooltip, Typography } from '@mui/material';
import { ShowInAppClient } from '@shared/ui-kit';
import {
  IconButton,
  type IconButtonProps,
} from '@shared/ui-kit/components/icon-button';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import type { ReactNode } from 'react';
import { DocumentFileListItem } from '../document-file-list-item/document-file-list-item';
import { EntityDocumentsLinkPanel } from '../entity-documents-link-panel';
import type { LinkedDocumentFileRow } from './entity-documents-section.utils';
import type { Document } from '@shared/data-access/types/engine-types';
import {
  useEntityDocumentsSection,
  type EntityDocumentsSectionProps,
} from './use-entity-documents-section';

export type { EntityDocumentsSectionProps };

const SectionHeading = ({ children }: { readonly children: ReactNode }) => (
  <Typography
    component="h3"
    variant="subtitle2"
    className="!mb-0 !block !w-full !text-base !font-semibold !uppercase !text-primary-medium"
  >
    {children}
  </Typography>
);

const EmptyState = ({ children }: { readonly children: ReactNode }) => (
  <div
    className="rounded-lg border border-section bg-page px-4 py-5 text-center"
    data-testid="entity-documents-empty"
  >
    <Typography
      component="p"
      variant="body2"
      className="!block !text-primary-medium"
    >
      {children}
    </Typography>
  </div>
);

const ActionIconButton = ({
  tooltip,
  ...props
}: IconButtonProps & { readonly tooltip: string }) => (
  <Tooltip title={tooltip} placement="top">
    <span className="inline-flex shrink-0 items-center justify-center">
      <IconButton {...props} />
    </span>
  </Tooltip>
);

const DocumentGroupHeader = ({
  title,
  fileCount,
  action,
}: {
  readonly title: string;
  readonly fileCount: number;
  readonly action?: ReactNode;
}) => (
  <header className="flex items-center gap-3">
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <p className="truncate text-base font-medium text-title-dark" title={title}>
        {title}
      </p>
      <p className="text-sm text-primary-medium">
        <Translation
          i18nKey="documents.file-count"
          ns="components"
          count={fileCount}
        />
      </p>
    </div>
    {action ?? null}
  </header>
);

const DocumentGroupFiles = ({
  fileRows,
  testIdPrefix,
  renderFileAction,
}: {
  readonly fileRows: readonly LinkedDocumentFileRow[];
  readonly testIdPrefix: string;
  readonly renderFileAction?: (row: LinkedDocumentFileRow) => ReactNode | undefined;
}) => {
  if (fileRows.length === 0) {
    return (
      <p className="text-sm text-primary-medium">
        <Translation
          i18nKey="documents.entity-section.files-empty"
          ns="components"
        />
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1">
      {fileRows.map(row => (
        <DocumentFileListItem
          key={row.rowKey}
          fileName={row.fileName}
          fileSize={row.fileSize}
          surface="section"
          testId={`${testIdPrefix}-file-${row.rowKey}`}
          action={renderFileAction?.(row)}
        />
      ))}
    </ul>
  );
};

const DocumentGroup = ({
  document,
  fileRows,
  fileCount,
  testIdPrefix,
  headerAction,
  renderFileAction,
}: {
  readonly document: Document;
  readonly fileRows: readonly LinkedDocumentFileRow[];
  readonly fileCount: number;
  readonly testIdPrefix: string;
  readonly headerAction?: ReactNode;
  readonly renderFileAction?: (row: LinkedDocumentFileRow) => ReactNode | undefined;
}) => (
  <li
    className="flex flex-col gap-2"
    data-testid={`${testIdPrefix}-${document.id}`}
  >
    <DocumentGroupHeader
      title={document.title}
      fileCount={fileCount}
      action={headerAction}
    />
    <DocumentGroupFiles
      fileRows={fileRows}
      testIdPrefix={testIdPrefix}
      renderFileAction={renderFileAction}
    />
  </li>
);

export const EntityDocumentsSection = defineComponent({
  displayName: 'EntityDocumentsSection',
  useSetup: useEntityDocumentsSection,
  View: ({
    t,
    linkedDocuments,
    relatableDocuments,
    hasRelatableDocuments,
    showRelatableSearch,
    relatableSearchQuery,
    onRelatableSearchQueryChange,
    onLinkDocument,
    onUnlinkDocument,
    onDownloadFile,
    buildFileRowsForDocument,
    downloadingRowKey,
    linkingDocumentId,
    isCatalogLoading,
    showLinkPanel,
    showUnlinkAction,
  }) => (
    <div
      className="flex w-full flex-col gap-6"
      data-testid="entity-documents-section"
    >
      <section
        className="flex w-full flex-col gap-3"
        data-testid="entity-documents-view-section"
      >
        <SectionHeading>
          <Translation i18nKey="documents.entity-section.title" ns="components" />
        </SectionHeading>

        {isCatalogLoading ? (
          <div className="flex items-center gap-2 py-1">
            <CircularProgress size={18} />
            <Typography
              component="span"
              variant="body2"
              className="text-primary-medium"
            >
              <Translation
                i18nKey="documents.entity-section.loading"
                ns="components"
              />
            </Typography>
          </div>
        ) : null}

        {!isCatalogLoading && linkedDocuments.length === 0 ? (
          <EmptyState>
            <Translation i18nKey="documents.entity-section.empty" ns="components" />
          </EmptyState>
        ) : null}

        {!isCatalogLoading && linkedDocuments.length > 0 ? (
          <ul className="flex flex-col gap-4" data-testid="entity-documents-list">
            {linkedDocuments.map(document => {
              const fileRows = buildFileRowsForDocument(document);
              const fileCount = document.files?.length ?? 0;
              const unlinkLabel = t('documents.entity-section.unlink-aria', {
                ns: 'components',
                title: document.title,
              });

              return (
                <DocumentGroup
                  key={document.id}
                  document={document}
                  fileRows={fileRows}
                  fileCount={fileCount}
                  testIdPrefix="entity-linked-document"
                  headerAction={
                    showUnlinkAction ? (
                      <ShowInAppClient>
                        <ActionIconButton
                          icon="Trashcan"
                          variant="icon"
                          size="lg"
                          color="main"
                          tooltip={unlinkLabel}
                          aria-label={unlinkLabel}
                          data-testid={`entity-document-unlink-${document.id}`}
                          onClick={() => onUnlinkDocument(document)}
                        />
                      </ShowInAppClient>
                    ) : undefined
                  }
                  renderFileAction={row => {
                    const isDownloading = downloadingRowKey === row.rowKey;
                    const downloadLabel = t(
                      'documents.entity-section.download-aria',
                      {
                        ns: 'components',
                        fileName: row.fileName,
                      },
                    );

                    return isDownloading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <ActionIconButton
                        icon="Download"
                        variant="icon"
                        size="lg"
                        color="main"
                        tooltip={downloadLabel}
                        aria-label={downloadLabel}
                        data-testid={`entity-document-download-${row.rowKey}`}
                        onClick={() => void onDownloadFile(row)}
                      />
                    );
                  }}
                />
              );
            })}
          </ul>
        ) : null}
      </section>

      <EntityDocumentsLinkPanel
        t={t}
        showLinkPanel={showLinkPanel}
        hasRelatableDocuments={hasRelatableDocuments}
        showRelatableSearch={showRelatableSearch}
        relatableSearchQuery={relatableSearchQuery}
        onRelatableSearchQueryChange={onRelatableSearchQueryChange}
        relatableDocuments={relatableDocuments}
        linkingDocumentId={linkingDocumentId}
        onLinkDocument={onLinkDocument}
      />
    </div>
  ),
});
